import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { Typography } from "@mui/material";
import { Stack } from "@mui/system";
import type { Resolver } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import type { Goal } from "api/models/Goal";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { StyledTextArea } from "routes/settings/components/shared/styles";
import { BaseButton } from "shared/atoms/BaseButton";
import { Dropdown } from "shared/atoms/inputs/Dropdown";
import { Notification } from "shared/atoms/Notification";
import { setGoal, updateGoal } from "shared/molecules/PatientGoal/queries/setGoal";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { CurrentPatientContext } from "utils/contexts";
import { isFiniteNumber } from "utils/number/is-finite-number";

import { generateDropdownValues } from "./utils/generateDropdownValues";

export type FormData = {
  free_text: string | null;
  pain: string | null;
  functionality: string | null;
  feeling: string | null;
};

export const validateForm: Resolver<FormData> = data => {
  const values = Object.entries(data).reduce((currentValues, [key, value]) => {
    if (value === undefined || value === "") {
      return { ...currentValues, [key]: null };
    }
    if (key !== "free_text" && value) {
      const parsedValue = parseInt(value, 10);
      const isNaN = Number.isNaN(parsedValue);
      const updatedValue = isNaN ? null : value;
      return { ...currentValues, [key]: updatedValue };
    }
    return { ...currentValues, [key]: value };
  }, {});
  return { values, errors: {} };
};

type GoalFormData = {
  id?: number;
  description?: string | "-" | null;
  functionality?: string | "-" | null;
  pain?: string | "-" | null;
  feeling?: string | "-" | null;
};

interface Props {
  id: number;
  onCancel?: () => void;
  isPhysicalFunctionAvailable: () => boolean;
  isFeelingAvailable: () => boolean;
  isPainAvailable: () => boolean;
  onSuccess: (data: Partial<Goal>) => void;
  currentGoal: GoalFormData;
}

const setOrUpdateGoal = (id: number, data: FormData, goalId: number | undefined) => {
  if (!goalId) return setGoal(id, data);
  return updateGoal(id, data, goalId);
};

export const PatientGoal: React.FC<Props> = ({
  id,
  onCancel,
  onSuccess,
  currentGoal,
  isPhysicalFunctionAvailable,
  isFeelingAvailable,
  isPainAvailable,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const getValuesRef = useRef<null | ((payload: string | string[]) => string | FormData)>(null);
  const { patient } = useContext(CurrentPatientContext);
  const { savedGoals, setSavedGoals } = useSavedInputsContext();
  const [goalState, setGoalState] = useState({ loading: false, error: false, success: false });
  const [allFieldsEmptyError, setAllFieldsEmptyError] = useState(false);

  useEffect(() => {
    return () => {
      if (getValuesRef.current) {
        const pain = getValuesRef?.current("pain") || "";
        const functionality = getValuesRef?.current("functionality") || "";
        const free_text = getValuesRef?.current("free_text") || "";
        const feeling = getValuesRef?.current("feeling") || "";

        if (
          pain !== currentGoal?.pain ||
          functionality !== currentGoal?.functionality ||
          free_text !== currentGoal?.description ||
          feeling !== currentGoal?.feeling
        ) {
          setSavedGoals((goals: Record<string, Partial<Goal>>) => ({
            ...goals,
            [patient?.id]: { pain, functionality, free_text, feeling },
          }));
        } else {
          setSavedGoals((all: Record<string, Partial<Goal>>) => {
            const goals = { ...all };
            delete goals[patient.id];
            return goals;
          });
        }
      }
    };
  }, []);

  const { register, handleSubmit, getValues } = useForm<FormData>({
    resolver: validateForm,
    defaultValues: {
      free_text: savedGoals[patient.id] ? savedGoals[patient.id]?.free_text : currentGoal?.description || "",
    },
  });

  useEffect(() => {
    getValuesRef.current = getValues;
  });

  const onSubmit = async (data: FormData) => {
    const allNull = Object.values(data).every(value => value === null);
    if (allNull) {
      setAllFieldsEmptyError(true);
    } else {
      try {
        setGoalState({ loading: true, error: false, success: false });
        const {
          data: { free_text, functionality, pain, id: goalId, feeling },
        } = await setOrUpdateGoal(id, data, currentGoal?.id);
        AnalyticsService.track(AnalyticsEvents.PATIENTS.UPDATE_GOAL, {
          pain,
          functionality,
          patient: id,
          goalDescription: free_text,
          feeling,
        });
        setGoalState({ loading: false, error: false, success: true });
        onSuccess({ free_text, functionality, pain, id: goalId, feeling });
      } catch (err) {
        setGoalState({ loading: false, error: true, success: false });
      }
    }
  };

  const selectedPainValue = resolvePainValue(savedGoals[patient.id]?.pain, currentGoal?.pain);
  const selectedFunctionalityValue = resolveFunctionalityValue(
    savedGoals[patient.id]?.functionality,
    currentGoal?.functionality
  );
  const selectedFeelingValue = resolveFeelingValue(savedGoals[patient.id]?.feeling, currentGoal?.feeling);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Stack pt={2}>
        <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
          {t("dashboard.tables.calls.goal.description")}
        </Typography>
        <StyledTextArea dataTestId="free_text" {...register("free_text")} rows={2} maxLength={300} />
      </Stack>

      {isPainAvailable() && (
        <Stack gap={1} pt={2}>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.pain")}
          </Typography>
          <DropdownWrapper>
            <Dropdown {...register("pain")} m="0" options={generateDropdownValues(10)} selected={selectedPainValue} />
          </DropdownWrapper>
        </Stack>
      )}

      {isPhysicalFunctionAvailable() && (
        <Stack gap={1} pt={2}>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.function")}
          </Typography>
          <DropdownWrapper>
            <Dropdown
              {...register("functionality")}
              m="0"
              options={generateDropdownValues(35)}
              selected={selectedFunctionalityValue}
            />
          </DropdownWrapper>
        </Stack>
      )}

      {isFeelingAvailable() && (
        <Stack gap={1} pt={2}>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("dashboard.tables.calls.goal.feeling")}
          </Typography>
          <DropdownWrapper>
            <Dropdown
              {...register("feeling")}
              m="0"
              options={generateDropdownValues(100)}
              selected={selectedFeelingValue}
            />
          </DropdownWrapper>
        </Stack>
      )}

      {allFieldsEmptyError && <Notification variant="danger">{t("errors.all_empty_goal_fields")}</Notification>}

      <ButtonContainer>
        <BaseButton
          variant="secondary"
          text={t("buttons.cancel")}
          onClick={onCancel}
          type="button"
          dataTestID="cancel_patient_goal"
        />
        <BaseButton
          variant="primary"
          text={t("buttons.save")}
          disabled={goalState.loading}
          type="submit"
          dataTestID="submit_patient_goal"
        />
      </ButtonContainer>
    </StyledForm>
  );
};

const resolvePainValue = (
  savedGoalPain: null | number | undefined,
  currentGoalPain: null | string | undefined
): string | undefined => {
  if (isFiniteNumber(savedGoalPain)) {
    return String(savedGoalPain);
  }
  return currentGoalPain ?? undefined;
};

const resolveFunctionalityValue = (
  savedGoalFunctionality: null | number | undefined,
  currentGoalFunctionality: null | string | undefined
): string | undefined => {
  if (isFiniteNumber(savedGoalFunctionality)) {
    return String(savedGoalFunctionality);
  }
  return currentGoalFunctionality ?? undefined;
};

const resolveFeelingValue = (
  savedGoalFeeling: null | number | undefined,
  currentGoalFeeling: null | string | undefined
): string | undefined => {
  if (isFiniteNumber(savedGoalFeeling)) {
    return String(savedGoalFeeling);
  }
  return currentGoalFeeling ?? undefined;
};

const StyledForm = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const DropdownWrapper = styled.div`
  max-width: 200px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
  }
`;
